import { Modal, message } from "antd";
import { useSelector } from "react-redux";
import { useState, useEffect } from "react";
import "./shareModal.scss";
import facebookIcon from "../../../assets/icons/facebook-logo.svg";
import twitterIcon from "../../../assets/icons/twitter.svg";
import instagramIcon from "../../../assets/icons/instagram.svg";
import starIcon from "../../../assets/icons/star-blue.svg";
import shareIcon from "../../../assets/icons/shareIcon.svg";
import discordIcon from "../../../assets/icons/discord-clean.svg";
import { shareCertificateRequest } from "../../../utils/apis/account";

const ShareModal = ({ isOpen, onClose, certificateUrl, certificateTitle, certificateId, certificateType = "payout" }) => {
  const idToken = useSelector((state) => state.auth.idToken);
  const [loadingPlatform, setLoadingPlatform] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const [certificateBlob, setCertificateBlob] = useState(null);
  const [backendShareUrl, setBackendShareUrl] = useState(null);

  // Preload the image and the share URL physically as soon as modal opens
  // This perfectly avoids losing the 'transient user activation' timer on mobile clicking "Share"!
  useEffect(() => {
    if (isOpen && certificateUrl) {
      // 1. Preload the Backend Share Link immediately
      const preloadShareUrl = async () => {
        let shareUrlToUse = certificateUrl;
        if (certificateId) {
          try {
            const response = await shareCertificateRequest(idToken, certificateId);
            if (response && response.url) {
              shareUrlToUse = response.url;
            }
          } catch (error) {
            console.error(error);
          }
        }
        setBackendShareUrl(shareUrlToUse);
      };
      preloadShareUrl();

      // 2. Preload the Image Blob physically to defeat CORS & network delays
      const preloadImage = async () => {
        try {
          const cacheBusterUrl = certificateUrl + (certificateUrl.includes("?") ? "&" : "?") + "t=" + new Date().getTime();

          const response = await fetch(cacheBusterUrl, {
            method: "GET",
            mode: "cors",
          });
          if (!response.ok) throw new Error("Direct fetch failed");
          const blob = await response.blob();
          setCertificateBlob(blob);
        } catch (e) {
          console.warn("Direct fetch blocked by CORS, trying proxy...");
          try {
            const proxyUrl = `https://api.allorigins.win/raw?url=${encodeURIComponent(certificateUrl)}`;
            const response = await fetch(proxyUrl);
            const blob = await response.blob();
            setCertificateBlob(blob);
          } catch (err) {
            console.error("Failed to preload blob", err);
          }
        }
      };
      preloadImage();
    } else if (!isOpen) {
      setCertificateBlob(null); // Clear when closed
      setBackendShareUrl(null);
    }
  }, [isOpen, certificateUrl, certificateId, idToken]);

  // Copy certificate link to clipboard
  const handleCopyLink = async () => {
    try {
      const shareUrl = backendShareUrl || certificateUrl;
      await navigator.clipboard.writeText(shareUrl);
      setLinkCopied(true);
      message.success("Certificate link copied to clipboard!");
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (e) {
      message.error("Failed to copy link");
    }
  };

  // Download handler - uses blob to prevent redirects
  const handleDownload = () => {
    if (!certificateUrl) return;

    const a = document.createElement("a");

    // If we have the blob, use it! It's much safer and prevents redirects.
    if (certificateBlob) {
      const blobUrl = window.URL.createObjectURL(certificateBlob);
      a.href = blobUrl;
      a.download = `${certificateTitle || "Certificate"}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(blobUrl);
    } else {
      // Fallback to direct link if blob isn't ready
      a.href = certificateUrl;
      a.download = `${certificateTitle || "Certificate"}.png`;
      a.target = "_blank";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };

  const handleShare = async (platform) => {
    setLoadingPlatform(platform);

    const shareUrlToUse = backendShareUrl || certificateUrl;

    // Dynamic share text based on certificate type
    let shareText = "Check out my payout certificate!";
    if (certificateType === "funded") {
      shareText = "Check out my funded certificate!";
    } else if (certificateType === "lifetime") {
      shareText = "Check out my lifetime payout certificate!";
    }

    const shareUrlEncoded = encodeURIComponent(shareUrlToUse);
    const shareTextEncoded = encodeURIComponent(shareText);

    // helper to try native sharing
    const tryNativeShare = async () => {
      let file = null;
      if (certificateBlob) {
        const fileType = certificateBlob.type.startsWith('image/') ? certificateBlob.type : 'image/png';
        file = new File([certificateBlob], "certificate.png", { type: fileType });
      }

      try {
        if (file && navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({
            text: shareText,
            files: [file]
          });
          return true;
        } else if (navigator.share) {
          await navigator.share({
            title: certificateTitle || 'Certificate',
            text: shareText,
            url: shareUrlToUse,
          });
          return true;
        }
      } catch (e) {
        // Return true even on AbortError so we don't trigger fallbacks
        if (e.name === 'AbortError') return true;
        console.error("Native share failed: ", e);
      }
      return false;
    };

    // More reliable mobile detection: check touch capability + screen size + user agent
    const isMobile = (
      ('ontouchstart' in window || navigator.maxTouchPoints > 0) &&
      window.innerWidth <= 1024
    ) || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

    const isMobileShare = !!navigator.share && isMobile;

    switch (platform) {
      case "native":
        await tryNativeShare();
        break;

      case "twitter":
        if (isMobileShare) {
          await tryNativeShare();
        } else {
          const twitterTextWithNewline = encodeURIComponent(`${shareText}\n\n${shareUrlToUse}`);
          window.open(
            `https://twitter.com/intent/tweet?text=${twitterTextWithNewline}`,
            "_blank",
            "width=600,height=500"
          );
        }
        break;

      case "facebook":
        if (isMobileShare) {
          await tryNativeShare();
        } else {
          window.open(
            `https://www.facebook.com/sharer/sharer.php?u=${shareUrlEncoded}`,
            "_blank",
            "width=600,height=500"
          );
        }
        break;

      case "instagram":
        if (isMobileShare) {
          await tryNativeShare();
        } else {
          handleDownload();
          try {
            await navigator.clipboard.writeText(shareUrlToUse);
            message.success("Image download started & link copied! Share on Instagram.");
          } catch (e) {
            message.info("Image download started! Share on Instagram.");
          }
        }
        break;

      case "discord":
        if (isMobileShare) {
          await tryNativeShare();
        } else {
          // Open Discord in a popup matching Facebook/X style
          window.open("https://discord.com/channels/@me", "_blank", "width=800,height=600");
          handleDownload();
          try {
            if (navigator.clipboard && navigator.clipboard.writeText) {
              await navigator.clipboard.writeText(shareUrlToUse);
              message.success("Link copied! Paste it in the Discord window.");
            } else {
              message.success("Image downloaded! Share it on Discord.");
            }
          } catch (e) {
            message.info("Image downloaded! Share on Discord.");
          }
        }
        break;

      default:
        break;
    }

    setLoadingPlatform(null);
    onClose();
  };

  const platforms = [
    ...(navigator.share
      ? [
        {
          name: "Share via Apps",
          key: "native",
          icon: shareIcon,
          color: "#4A90E2",
        },
      ]
      : []),
    {
      name: "X (Twitter)",
      key: "twitter",
      icon: twitterIcon,
      color: "#000",
    },
    {
      name: "Instagram",
      key: "instagram",
      icon: instagramIcon,
      gradient: "linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)",
      mobileOnly: true,
    },
    {
      name: "Facebook",
      key: "facebook",
      icon: facebookIcon,
      color: "#1877F2",
    },
    {
      name: "Discord",
      key: "discord",
      icon: discordIcon,
      color: "#5865F2",
    },
    // {
    //   name: "Testimonials",
    //   key: "testimonials",
    //   icon: starIcon,
    //   color: "#36d66b",
    //   disabled: true,
    // },
  ];

  return (
    <Modal
      open={isOpen}
      onCancel={() => {
        setLinkCopied(false);
        setLoadingPlatform(null);
        onClose();
      }}
      footer={null}
      centered
      className="share-certificate-modal"
      width={400}
      style={{
        maxWidth: "calc(100vw - 32px)",
        top: 0,
      }}
      styles={{
        body: { padding: 0 },
      }}
    >
      <div className="share-modal-content">
        <h2 className="share-modal-title">Share Certificate</h2>
        <p className="share-modal-subtitle">Share your achievement on social media</p>

        {/* Certificate image preview */}
        <div className="share-certificate-preview">
          <img
            src={certificateUrl}
            alt="Certificate preview"
          />
        </div>

        {/* Action buttons */}
        <div className="share-action-buttons">
          {/* <button
                        className={`share-copy-link-btn ${linkCopied ? "copied" : ""}`}
                        onClick={handleCopyLink}
                    >
                        {linkCopied ? "✓ Copied!" : "📋 Copy Link"}
                    </button> */}
          <button
            className="share-download-btn"
            onClick={handleDownload}
          >
            ⬇ Download Image
          </button>
        </div>

        <p className="share-helper-text">Download the image and upload it when sharing for best results</p>

        <div className="share-platforms">
          {platforms.map((platform) => (
            <div
              key={platform.key}
              className={`share-platform-item ${platform.disabled || loadingPlatform === platform.key ? "disabled" : ""} ${platform.mobileOnly ? "mobile-only" : ""}`}
              onClick={() => !platform.disabled && loadingPlatform !== platform.key && handleShare(platform.key)}
            >
              <div
                className="platform-icon"
                style={{ background: platform.gradient || platform.color }}
              >
                <img
                  src={platform.icon}
                  alt={platform.name}
                />
              </div>
              <span className="platform-name">
                {loadingPlatform === platform.key ? "Sharing..." : platform.name}
              </span>
              {/* {platform.key === "testimonials" && (
                  <span className="coming-soon-badge">Coming Soon</span>
                )} */}
            </div>
          ))}
        </div>
      </div>
    </Modal>
  );
};

export default ShareModal;
